import { tracked } from '@glimmer/tracking';
import { TrackedMap } from 'tracked-built-ins';
import { SyncRelay, ENVELOPE, sameSite } from './sync-relay';

const STATE_KEY = 'dubbletrack:playback:state';
const CMD_KEY = 'dubbletrack:playback:cmd';
const CHANNEL_NAME = 'dubbletrack-playback';
const OVERLAP_KEY = 'dubbletrack:playback:overlap';
const STORAGE_WRITE_GAP_MS = 100;

// Several tabs can share a window.name.
const TAB_ID = `${window.name || 'tab'}:${Math.random().toString(36).slice(2, 10)}`;
const OPENED_AT = Date.now();
window.dubbletrackTabId = TAB_ID;

// A WindowProxy on another origin throws on property access.
function tabIdOf(ref) {
  try {
    return ref.dubbletrackTabId ?? null;
  } catch (e) {
    return null;
  }
}

/**
 * SyncChannel
 *
 * Cross-tab playback coordination via localStorage and BroadcastChannel.
 * Owns the transport wiring, tab identity, reactive remote state, and
 * command execution — so PlaybackService only deals with stereo events.
 *
 * Reactive properties (usable in templates/getters):
 *   remoteIsPlaying   — boolean | null (null = no info yet)
 *   remoteUrl         — URL playing in another tab
 *   remoteAiringId    — airing playing in another tab, null for the live stream
 *   remoteProgress    — 0..1 through that airing, null when unknown or live
 *   remoteTabId       — ID of the tab that last reported state (persists after pause)
 *   remoteWindowName  — window.name of that tab (for focus/bring-to-front)
 *   remoteShowTitle   — the show that tab is playing, as it reported it
 *   remoteIsLive      — true when that tab is on the live stream
 *   remoteIsBlocked   — true when that tab's browser refused to start the sound without a click
 *   remoteImageUrl    — small square art for what that tab is playing
 *   remoteWindowKind  — 'popup' when that tab was opened as our toolbar-less window, else 'tab'
 *   isPlayingRemotely — true if another tab is actively playing
 *   isPlayingLocally  — true if this tab's stereo is playing
 *   isPlayingAnywhere — either local or remote is playing
 *
 * Tab registry (best-effort — not updated for hard crashes/kills):
 *   openTabs              — Map<tabId, windowName> of tabs seen since this tab opened
 *   isTabOpen(windowName) — true if a tab with that window.name is in the registry
 *   tabIdForWindowName(n) — returns the tabId for a named window, else the latest player tab, or null
 */
export class SyncChannel {
  _channel = null;
  _relay = null;
  _storageHandler = null;
  _stereo = null;

  // Best-effort registry of currently-open tabs. Populated via BroadcastChannel
  // register/unregister messages. Not reliable for hard crashes or killed processes.
  openTabs = new TrackedMap(); // tabId -> windowName
  // Tabs that host the player, whatever their window.name: a bookmarked or hand-opened player page is one too.
  playerTabs = new TrackedMap(); // tabId -> openedAt

  // WindowProxy kept from a window.open() call. When present and not closed,
  // focus() on it is guaranteed to work (opener relationship).
  _playerWindowRef = null;
  // The relay frame is partitioned away from a player on another site, so the tab that
  // opened the player talks to it directly, and the opener relationship survives partitioning.
  _playerOrigin = null;
  @tracked _directOrigin = null;
  _helloTimer = null;

  _announcement(type) {
    return { type, tabId: TAB_ID, windowName: window.name || null, openedAt: OPENED_AT, isPlayer: this._isPlayer };
  }

  openWindow(url, windowName, features) {
    this._playerWindowRef = window.open(url, windowName, features);
    let origin = new URL(url, window.location.href).origin;
    if (this._playerWindowRef && !sameSite(origin, window.location.origin)) {
      this._playerOrigin = origin;
      this._greetPlayerWindow();
    }
    return this._playerWindowRef;
  }

  // The player is not listening until it has booted, so say hello until it answers.
  _greetPlayerWindow() {
    clearInterval(this._helloTimer);
    let attempts = 0;
    this._helloTimer = setInterval(() => {
      let ref = this._playerWindowRef;
      if (!ref || ref.closed || this._directOrigin || attempts++ > 40) {
        clearInterval(this._helloTimer);
        return;
      }
      ref.postMessage({ [ENVELOPE]: this._announcement('hello') }, this._playerOrigin);
    }, 500);
  }

  _handleDirectMessage(event) {
    let data = event.data?.[ENVELOPE];
    if (!data) return;
    let fromPlayer = this._playerWindowRef && event.source === this._playerWindowRef && event.origin === this._playerOrigin;
    let fromOpener = window.opener && event.source === window.opener && !sameSite(event.origin, window.location.origin);
    if (!fromPlayer && !fromOpener) return;
    if (data.type === 'hello') {
      this._directOrigin = event.origin;
      if (fromOpener) {
        this._directPost(this._announcement('hello'));
        this._replyWithCurrentState();
      }
      this._handleChannelMessage({ ...data, type: 'register' });
      return;
    }
    this._handleChannelMessage(data);
  }

  _directPost(message) {
    if (!this._directOrigin) return;
    let target = this._playerWindowRef && !this._playerWindowRef.closed ? this._playerWindowRef : window.opener;
    target?.postMessage({ [ENVELOPE]: message }, this._directOrigin);
  }

  // Only the opener can bring a window forward, and two popups share a name, so only a window this page opened that is the
  // playing tab counts as reachable.
  get canFocusWindow() {
    let ref = this._playerWindowRef;
    let tabId = this.remoteTabId;
    return !!ref && !ref.closed && (!tabId || tabIdOf(ref) === tabId);
  }

  focusWindow() {
    if (!this.canFocusWindow) {
      return false;
    }
    this._playerWindowRef.focus();
    return true;
  }

  @tracked remoteIsPlaying = null;
  @tracked remoteUrl = null;
  @tracked remoteAiringId = null;
  @tracked remoteProgress = null;
  @tracked remoteTabId = null;
  @tracked remoteWindowName = null;
  @tracked remoteShowTitle = null;
  @tracked remoteIsLive = null;
  @tracked remoteIsBlocked = false;
  @tracked remoteImageUrl = null;
  @tracked remoteShowSlug = null;
  @tracked remoteWindowKind = null;

  // relayTo: the player's URL when it lives on another origin than this page.
  constructor({ stereo, describeSound, onOpenEpisode, onGoLive, onDismissed, relayTo, isPlayer = false, exclusive = !SyncChannel.overlapAllowed } = {}) {
    this._stereo = stereo;
    this._exclusive = exclusive;
    this._isPlayer = isPlayer;
    this._onDismissed = onDismissed;
    this._describeSound = describeSound;
    this._onOpenEpisode = onOpenEpisode;
    this._onGoLive = onGoLive;
    if (relayTo && sameSite(relayTo, window.location.origin)) {
      this._relay = new SyncRelay(relayTo, (data) => this._handleChannelMessage(data));
    }

    // Seed from localStorage immediately so state is accurate before any
    // BroadcastChannel or storage events have had a chance to fire.
    let saved = this.savedState;
    if (saved) {
      this.remoteIsPlaying = saved.isPlaying;
      this.remoteUrl = saved.url;
      this.remoteAiringId = saved.airingId ?? null;
      this.remoteProgress = saved.progress ?? null;
      this.remoteShowTitle = saved.showTitle ?? null;
      this.remoteIsLive = saved.isLive ?? null;
      this.remoteIsBlocked = !!saved.isBlocked;
      this.remoteImageUrl = saved.imageUrl ?? null;
      this.remoteShowSlug = saved.showSlug ?? null;
      this.remoteWindowKind = saved.windowKind ?? null;
      if (saved.tabId) this.remoteTabId = saved.tabId;
      if (saved.windowName) this.remoteWindowName = saved.windowName;
    }

    this._storageHandler = this._handleStorageEvent.bind(this);
    window.addEventListener('storage', this._storageHandler);
    this._directHandler = this._handleDirectMessage.bind(this);
    window.addEventListener('message', this._directHandler);

    if (typeof BroadcastChannel !== 'undefined') {
      this._channel = new BroadcastChannel(CHANNEL_NAME);
      this._channel.onmessage = (event) => this._handleChannelMessage(event.data);

      // Announce ourselves to other tabs.
      this._post(this._announcement('register'));

      // Unregister on close (best-effort — won't fire on hard crash/kill).
      // pagehide, so a reload counts as a stop. iOS Safari also fires it when the
      // tab is backgrounded or the screen locks, with persisted set and the audio
      // still going, so that one is not a stop.
      this._pageHideHandler = (event) => {
        if (event.persisted) return;
        if (this._stereo?.currentSound?.isPlaying) {
          this._describeSound?.(this._stereo.currentSound, false);
        }
        this._post({ type: 'unregister', tabId: TAB_ID });
      };
      window.addEventListener('pagehide', this._pageHideHandler);

      // Other tabs may have missed state broadcast while this one was suspended.
      this._pageShowHandler = () => this._replyWithCurrentState();
      window.addEventListener('pageshow', this._pageShowHandler);

      // Ask any open tabs to re-broadcast their live state so we can initialize
      // with something fresher than what's in localStorage.
      Promise.resolve().then(() => {
        this._post({ type: 'request-state' });
      });
    }
  }

  _post(message) {
    this._channel?.postMessage(message);
    this._relay?.post(message);
    this._directPost(message);
  }

  // ---------------------------------------------------------------------------
  // Reactive getters
  // ---------------------------------------------------------------------------

  get relayOrigin() {
    return this._relay?.origin ?? null;
  }

  get directOrigin() {
    return this._directOrigin;
  }

  // Set localStorage['dubbletrack:playback:overlap'] to let tabs play over each other while debugging.
  static get overlapAllowed() {
    try {
      return !!localStorage.getItem(OVERLAP_KEY);
    } catch (e) {
      return false;
    }
  }

  get isPlayingRemotely() {
    return this.remoteIsPlaying === true;
  }

  get isPlayingLocally() {
    return this._stereo.currentSound?.isPlaying ?? false;
  }

  get isPlayingAnywhere() {
    return this.isPlayingLocally || this.isPlayingRemotely;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Write state to localStorage and broadcast via BroadcastChannel.
   * tabId and timestamp are added automatically.
   * @param {{ isPlaying: boolean, isBlocked: boolean, url: string, airingId: string|null, progress: number|null, isLive: boolean, showTitle: string|null, showSlug: string|null, imageUrl: string|null, windowKind: 'popup'|'tab', windowName: string|null }} state
   */
  broadcastState(state) {
    let payload = { ...state, tabId: TAB_ID, at: Date.now() };
    this._writeStorage(STATE_KEY, payload);
    this._post({ type: 'state', ...payload });
  }

  // Every tab hears every write, and a tab that answers a write with a write of its own would run the others ragged.
  // A burst collapses to the latest value, written once the key has been quiet for a moment.
  _writeStorage(key, payload) {
    let timers = (this._writeTimers ??= new Map());
    let lastWrites = (this._lastWrites ??= new Map());
    let flush = () => {
      timers.delete(key);
      lastWrites.set(key, Date.now());
      try {
        localStorage.setItem(key, JSON.stringify(payload));
      } catch (e) {}
    };
    clearTimeout(timers.get(key));
    if (Date.now() - (lastWrites.get(key) ?? 0) >= STORAGE_WRITE_GAP_MS) {
      flush();
    } else {
      timers.set(key, setTimeout(flush, STORAGE_WRITE_GAP_MS));
    }
  }

  /**
   * Send a command to other tabs.
   * @param {'pause'|'play'|'toggle'|'open-episode'|'go-live'|'close'} command
   * @param {string} [targetTabId] - If provided, only that tab will act on it.
   * @param {object} [details] - For open-episode: { episodeId, at }
   */
  sendCommand(command, targetTabId = null, details = {}) {
    // Nested: the payload's own `at` is a timestamp.
    let payload = { command, details, at: Date.now() };
    if (targetTabId) payload.targetTabId = targetTabId;
    this._writeStorage(CMD_KEY, payload);
    this._post({ type: 'command', ...payload });
    this._confirmStateAfterCommand(targetTabId);
  }

  // A feed switch can swallow the played event. Twice: a loaded sound answers fast, a loading one slowly.
  // A tab that answers neither ask is gone: a crash or a hard close never sends unregister.
  _confirmStateAfterCommand(targetTabId = null) {
    this._confirmTimers?.forEach(clearTimeout);
    let ask = () => this._post({ type: 'request-state' });
    this._awaitingTabId = targetTabId;
    this._confirmTimers = [setTimeout(ask, 800), setTimeout(ask, 3000), setTimeout(() => this._forgetSilentTab(), 4500)];
  }

  _forgetSilentTab() {
    let tabId = this._awaitingTabId;
    this._awaitingTabId = null;
    if (!tabId || !this.openTabs.has(tabId)) return;
    this._handleChannelMessage({ type: 'unregister', tabId });
  }

  _heardFrom(tabId) {
    if (tabId && tabId === this._awaitingTabId) {
      this._awaitingTabId = null;
    }
  }

  /**
   * Returns true if a tab with the given window.name is currently registered.
   * Best-effort — won't reflect hard crashes or killed processes.
   * @param {string} windowName
   * @returns {boolean}
   */
  isTabOpen(windowName) {
    for (let [, name] of this.openTabs) {
      if (name === windowName) return true;
    }
    return false;
  }

  /**
   * Returns the tabId to address for a window.name: the tab that last reported
   * playback under that name, else the most recently registered one, else null.
   * @param {string} windowName
   * @returns {string|null}
   */
  tabIdForWindowName(windowName) {
    if (this.remoteWindowName === windowName && this.remoteTabId && this.openTabs.has(this.remoteTabId)) {
      return this.remoteTabId;
    }
    let latest = null;
    for (let [tabId, name] of this.openTabs) {
      if (name === windowName) latest = tabId;
    }
    if (latest) return latest;
    for (let tabId of this.playerTabs.keys()) latest = tabId;
    return latest;
  }

  destroy() {
    this._confirmTimers?.forEach(clearTimeout);
    this._writeTimers?.forEach(clearTimeout);
    window.removeEventListener('storage', this._storageHandler);
    window.removeEventListener('message', this._directHandler);
    window.removeEventListener('pagehide', this._pageHideHandler);
    window.removeEventListener('pageshow', this._pageShowHandler);
    clearInterval(this._helloTimer);
    this._channel?.close();
    this._channel = null;
    this._relay?.destroy();
    this._relay = null;
  }

  // ---------------------------------------------------------------------------
  // Internal
  // ---------------------------------------------------------------------------

  get savedState() {
    try {
      return JSON.parse(localStorage.getItem(STATE_KEY));
    } catch (e) {
      return null;
    }
  }

  _applyState({ isPlaying, isBlocked, url, airingId, progress, isLive, showTitle, showSlug, imageUrl, windowKind, tabId, windowName }) {
    if (this._exclusive && isPlaying && tabId && tabId !== TAB_ID) {
      if (this._stereo?.currentSound?.isPlaying) {
        this._stereo.currentSound.pause();
      }
      // A window still waiting for its click has been passed over.
      this._onDismissed?.();
    }
    this.remoteIsPlaying = isPlaying;
    this.remoteUrl = url;
    this.remoteAiringId = airingId ?? null;
    this.remoteProgress = progress ?? null;
    this.remoteShowTitle = showTitle ?? null;
    this.remoteIsLive = isLive ?? null;
    this.remoteIsBlocked = !!isBlocked;
    this.remoteImageUrl = imageUrl ?? null;
    this.remoteShowSlug = showSlug ?? null;
    this.remoteWindowKind = windowKind ?? null;
    // Keep tabId/windowName even after the remote tab pauses — we still need
    // to know who to send the play command or focus request back to.
    if (tabId) this.remoteTabId = tabId;
    if (windowName) this.remoteWindowName = windowName;
  }

  _executeCommand({ command, details = {} }) {
    let sound = this._stereo.currentSound;
    if (command === 'open-episode') {
      this._onOpenEpisode?.(details);
      return;
    }
    if (command === 'go-live') {
      this._onGoLive?.();
      return;
    }
    // A listener asked from another page for this window to go away. Only a popup can close itself, so a tab pauses instead.
    if (command === 'close') {
      sound?.pause();
      this._onDismissed?.();
      window.close();
      return;
    }
    if (!sound) return;
    if (command === 'pause' && sound.isPlaying) {
      sound.pause();
    } else if (command === 'play' && !sound.isPlaying) {
      sound.play();
    } else if (command === 'toggle') {
      sound.togglePause();
    }
  }

  // Two players under one name would both answer commands. The older idle one bows out to the newer.
  _yieldToDuplicate({ tabId, windowName, openedAt }) {
    if (!window.name || windowName !== window.name || tabId === TAB_ID) return;
    if (!openedAt || openedAt <= OPENED_AT) return;
    if (this._stereo.currentSound?.isPlaying) return;
    window.close();
  }

  _replyWithCurrentState() {
    let sound = this._stereo.currentSound;
    if (sound) {
      this._describeSound?.(sound, !!sound.isPlaying);
    }
    // Re-announce registration so newly opened tabs can populate their openTabs map.
    this._post(this._announcement('register'));
  }

  _handleStorageEvent(event) {
    if (event.key === STATE_KEY && event.newValue) {
      try {
        this._applyState(JSON.parse(event.newValue));
      } catch (e) {}
    } else if (event.key === CMD_KEY && event.newValue) {
      try {
        let cmd = JSON.parse(event.newValue);
        if (!cmd.targetTabId || cmd.targetTabId === TAB_ID) {
          this._executeCommand(cmd);
        }
      } catch (e) {}
    }
  }

  _handleChannelMessage(data) {
    if (!data) return;
    this._heardFrom(data.tabId);
    if (data.type === 'state') {
      this._applyState(data);
    } else if (data.type === 'command') {
      if (!data.targetTabId || data.targetTabId === TAB_ID) {
        this._executeCommand(data);
      }
    } else if (data.type === 'request-state') {
      this._replyWithCurrentState();
    } else if (data.type === 'register') {
      // Keeps insertion order "most recent last".
      this.openTabs.delete(data.tabId);
      this.openTabs.set(data.tabId, data.windowName);
      this.playerTabs.delete(data.tabId);
      if (data.isPlayer) this.playerTabs.set(data.tabId, data.openedAt);
      this._yieldToDuplicate(data);
    } else if (data.type === 'unregister') {
      this.openTabs.delete(data.tabId);
      this.playerTabs.delete(data.tabId);
      if (data.tabId === this.remoteTabId) {
        this.remoteIsPlaying = false;
      }
    }
  }
}
